/**
 * Attempts table of one test run (epic 031 task 06; FR-B29 — what a run
 * displays: request status, response code, sanitized body, timestamp). Every
 * `distribution_attempt` stamped with the run: `#`, kind, receiving campaign ·
 * advertiser, outcome, reason, HTTP, the masked request summary, the sanitized
 * response excerpt (collapsible once it runs past 300 characters), started,
 * finished. A routed hop renders indented under its source row. The raw
 * partner response body is never part of this payload (FR-B29) — only the
 * sanitized excerpt.
 */

import type { AttemptKind, AttemptOutcome, TestRunAttempt } from '@leads-platform/shared';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tag } from '../../components/ui/tag';
import { fmtDate } from '../../lib/format';

/** Long excerpts collapse behind a toggle; short ones render inline. */
const EXCERPT_COLLAPSE_LENGTH = 300;

/** Outcome → Tag tone (the Distribution screen's Attempts log mapping). */
const OUTCOME_TONES: Record<AttemptOutcome, 'sky' | 'amber' | 'rose' | 'neutral'> = {
  accepted: 'sky',
  duplicate: 'amber',
  rejected: 'rose',
  suppressed: 'neutral',
  error: 'rose',
  timeout: 'rose',
  skipped: 'neutral',
};

/** Plain-language outcome labels. */
const OUTCOME_LABELS: Record<AttemptOutcome, string> = {
  accepted: 'Accepted',
  duplicate: 'Duplicate',
  rejected: 'Rejected',
  suppressed: 'Suppressed',
  error: 'Error',
  timeout: 'Timeout',
  skipped: 'Skipped',
};

/** Plain-language kind labels. */
const KIND_LABELS: Record<AttemptKind, string> = {
  pre_ping: 'Pre-ping',
  send: 'Send',
};

/**
 * Plain-language skip-reason map (QA #81, task 038-04): every code
 * `PartnerSendSkipReason` can carry
 * (`packages/api/src/modules/partners/partner-send.types.ts:38–49` — not
 * importable here, admin-client depends only on `@leads-platform/shared`),
 * including the builder's own `send_mode_click_out` / `adapter_not_built`
 * spellings (already part of that union). `credential_config_invalid` gets
 * the actionable sentence the run detail was missing — the builder's own
 * `detail` string is written to a warn log and dropped
 * (`partner-send.service.ts:128–137`), so this is the plain-language
 * substitute, not a plumbed-through value. An unmapped reason string is not
 * a bug here: it still renders verbatim (below), and the raw code stays
 * visible under every mapped reason too, so a support conversation can match
 * it against a log line.
 *
 * `credential_config_invalid` names both placeholder spellings (038-04 fix
 * round): `TestRunAttempt.reason` carries no `placeholder_syntax`, so — unlike
 * the endpoint form, which knows the endpoint's own syntax and can name one —
 * this cell cannot tell which syntax the failing endpoint used.
 */
const SKIP_REASON_LABELS: Record<string, string> = {
  crm_config_invalid: "The advertiser's CRM configuration is invalid for this send.",
  endpoint_unavailable: 'The partner endpoint is missing or retired.',
  endpoint_values_invalid: "The campaign's values fail the endpoint's declared variables.",
  send_mode_click_out: 'This partner is click-out only — no lead is sent to it.',
  adapter_not_built: 'No send adapter has been built for this partner yet.',
  partner_mode_off: 'Partner sends are switched off for this build.',
  credential_missing: 'No credential is attached to this endpoint or campaign.',
  credential_revoked: 'The attached credential has been revoked.',
  credential_key_unavailable:
    "The encryption key needed to decrypt the credential isn't available.",
  credential_decrypt_failed: 'The credential failed to decrypt.',
  credential_config_invalid:
    "This credential's placement is unset in the transport — add %credential% (percent syntax) or [[CREDENTIAL-HERE]] (bracket syntax) to a request header value or the URL so the endpoint knows where the token goes.",
};

/** Local HH:mm:ss — the time half of a timestamp. */
function localTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toTimeString().slice(0, 8);
}

function When({ iso }: { iso: string }) {
  return (
    <TableCell className="whitespace-nowrap">
      <span className="font-mono text-xs text-ink">{fmtDate(iso)}</span>
      <span className="block font-mono text-xs text-faint">{localTime(iso)}</span>
    </TableCell>
  );
}

/**
 * The Reason cell's content: a mapped code renders its plain-language
 * sentence plus the raw code (small, monospace) underneath; an unmapped code
 * (or a code outside `PartnerSendSkipReason`, e.g. a raw HTTP-classification
 * reason) renders verbatim, unchanged from before this task; no reason
 * renders the existing em-dash.
 */
function Reason({ reason }: { reason: string | null }) {
  if (reason === null) {
    return <>—</>;
  }
  const label = SKIP_REASON_LABELS[reason];
  if (label === undefined) {
    return <>{reason}</>;
  }
  return (
    <>
      <span>{label}</span>
      <span className="block font-mono text-xs text-faint">{reason}</span>
    </>
  );
}

/** Sanitized response excerpt — monospace; collapsible once it runs long. */
function Excerpt({ excerpt }: { excerpt: string }) {
  if (excerpt.length < EXCERPT_COLLAPSE_LENGTH) {
    return (
      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted">{excerpt}</pre>
    );
  }
  return (
    <details>
      <summary className="cursor-pointer text-xs font-semibold text-accent">
        Show excerpt ({excerpt.length} characters)
      </summary>
      <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-paper p-2 font-mono text-xs text-muted">
        {excerpt}
      </pre>
    </details>
  );
}

/**
 * The rows in display order: top-level attempts by `attemptNo`, each followed
 * by the hops routed from it (also by `attemptNo`).
 */
function orderRows(attempts: TestRunAttempt[]): TestRunAttempt[] {
  const byAttemptNo = (a: TestRunAttempt, b: TestRunAttempt) => a.attemptNo - b.attemptNo;
  const top = attempts.filter((a) => a.routedFromAttemptId === null).sort(byAttemptNo);
  const routed = attempts.filter((a) => a.routedFromAttemptId !== null);
  const out: TestRunAttempt[] = [];
  for (const row of top) {
    out.push(row);
    for (const hop of routed.filter((a) => a.routedFromAttemptId === row.id).sort(byAttemptNo)) {
      out.push(hop);
    }
  }
  // A routed row whose source is outside this run's rows still shows — last.
  const placed = new Set(out.map((a) => a.id));
  for (const hop of routed.sort(byAttemptNo)) {
    if (!placed.has(hop.id)) out.push(hop);
  }
  return out;
}

interface AttemptsTableProps {
  attempts: TestRunAttempt[];
}

export function AttemptsTable({ attempts }: AttemptsTableProps) {
  const rows = orderRows(attempts);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">#</TableHead>
          <TableHead>Kind</TableHead>
          <TableHead>Receiving campaign · advertiser</TableHead>
          <TableHead>Outcome</TableHead>
          <TableHead>Reason</TableHead>
          <TableHead>HTTP</TableHead>
          <TableHead>Request</TableHead>
          <TableHead>Response excerpt</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Finished</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={10} className="py-6 text-center text-muted-foreground">
              No attempts recorded yet.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((attempt) => {
            const routed = attempt.routedFromAttemptId !== null;
            return (
              <TableRow key={attempt.id}>
                <TableCell className={'font-mono text-xs ' + (routed ? 'pl-10' : '')}>
                  {routed && <span className="mr-1 text-faint">↳</span>}
                  {attempt.attemptNo}
                </TableCell>
                <TableCell className="text-muted">
                  {KIND_LABELS[attempt.kind]}
                  {routed && <span className="block text-xs text-faint">routed hop</span>}
                </TableCell>
                <TableCell className="text-muted">
                  {attempt.receivingCampaignName ?? '—'}
                  {attempt.advertiserName ? (
                    <span className="block text-xs text-faint">{attempt.advertiserName}</span>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Tag tone={OUTCOME_TONES[attempt.outcome]}>{OUTCOME_LABELS[attempt.outcome]}</Tag>
                </TableCell>
                <TableCell className="max-w-[180px] break-words text-xs text-muted">
                  <Reason reason={attempt.reason} />
                </TableCell>
                <TableCell className="font-mono text-xs text-muted">
                  {attempt.httpStatus ?? '—'}
                </TableCell>
                <TableCell className="max-w-[240px]">
                  {attempt.requestSummary.method || attempt.requestSummary.url ? (
                    <span className="break-all font-mono text-xs text-muted">
                      {attempt.requestSummary.method ?? ''} {attempt.requestSummary.url ?? ''}
                    </span>
                  ) : (
                    <span className="text-xs text-faint">No advertiser request</span>
                  )}
                </TableCell>
                <TableCell className="max-w-[280px]">
                  {attempt.responseExcerpt ? (
                    <Excerpt excerpt={attempt.responseExcerpt} />
                  ) : (
                    <span className="text-xs text-faint">—</span>
                  )}
                </TableCell>
                <When iso={attempt.startedAt} />
                {attempt.finishedAt ? (
                  <When iso={attempt.finishedAt} />
                ) : (
                  <TableCell className="text-xs text-faint">—</TableCell>
                )}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
